// Company funnel widget — the whole live pipeline in one row (Ship 2).
// Counts only (no money, no names) — display grouping via the shared buckets.

import { useEffect, useMemo, useState } from "react";
import { supabase } from "../../../lib/supabase";
import { NON_LIVE_STAGES, STAGE_BUCKETS } from "./stageBuckets";

export default function CompanyFunnelWidget() {
  const [stages, setStages] = useState<string[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Paged past the 1,000-row cap (repo rule for bulk deals reads).
      const PAGE = 1000;
      const all: string[] = [];
      for (let from = 0; ; from += PAGE) {
        const { data, error } = await supabase
          .from("deals")
          .select("id,stage")
          .not("stage", "in", `(${NON_LIVE_STAGES.join(",")})`)
          .order("id")
          .range(from, from + PAGE - 1);
        if (cancelled) return;
        if (error || !data) {
          // Never present a failed/partial read as "0 live" fact.
          if (!cancelled) setFailed(true);
          return;
        }
        for (const r of data as Array<{ stage: string | null }>) if (r.stage) all.push(r.stage);
        if (data.length < PAGE) break;
      }
      if (!cancelled) setStages(all);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const buckets = useMemo(() => {
    if (!stages) return null;
    return STAGE_BUCKETS.map((b) => ({
      label: b.label,
      n: stages.filter((s) => b.stages.includes(s)).length,
    }));
  }, [stages]);

  const reinspect = buckets?.find((b) => b.label === "Reinspect")?.n ?? 0;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="flex items-center gap-2 mb-2">
        <span>📋</span>
        <span className="font-bold text-sm text-bardin-ink">Company Pipeline</span>
        <span className="ml-auto text-xs font-bold text-slate-500">
          {stages ? `${stages.length} live` : "…"}
        </span>
      </div>
      {failed ? (
        <div className="text-xs text-slate-400 py-2">Couldn't load the pipeline — try a refresh.</div>
      ) : buckets === null ? (
        <div className="h-9 rounded-lg bg-slate-100 animate-pulse" />
      ) : (
        <div className="flex flex-wrap gap-1.5 text-[10px] font-bold">
          {buckets
            .filter((b) => b.n > 0)
            .map((b) => (
              <span
                key={b.label}
                className={`px-2 py-0.5 rounded-full ${
                  b.label === "Reinspect" && b.n >= 20
                    ? "bg-amber-50 text-amber-700"
                    : "bg-slate-100 text-slate-600"
                }`}
              >
                {b.label} {b.n}
              </span>
            ))}
        </div>
      )}
      {reinspect >= 20 && (
        <div className="text-[10px] text-amber-700 font-semibold mt-2">
          ⚠ {reinspect} deals in reinspections — the biggest bulge in the funnel.
        </div>
      )}
    </div>
  );
}
